/**
 * The browser's half of a membership transaction: begin it, run its 3Halves leg under a fence and
 * a lock, report what happened, and resume a leg that is still due on any later page.
 *
 * The server's half is App\Loyalty\Membership (docs/subsystems/loyalty-withdrawal.md). What this
 * file adds is only what a browser can: the fan's access token for 3Halves, and presence. It
 * decides nothing about the transaction's state -- every answer comes back from the server and is
 * written to the marker as it came.
 *
 * Pure and injected, so membership.test.js drives the whole sequence with fakes; ./index.js wires
 * the real browser.
 */

import { isDue, wantsVerify } from './marker.js';
import { FenceError, LEG } from './processorLeg.js';
import { SKIPPED } from './lock.js';

/** A begin that failed this way may be resent with the SAME id: the server treats it as one. */
const RESENDABLE = [ 'timeout', 'network' ];

/**
 * Reasons that mean "this install cannot run transactions": no URLs in a page cached before them,
 * or a table that is not installed yet. The caller then uses the plain consent write, so a fan can
 * always leave.
 */
const FALLBACK = [ 'unconfigured', 'membership_unavailable', 'rest_no_route' ];

/** How long a rejoin that superseded a leave keeps looking for a late raise, in seconds. */
export const VERIFY_WINDOW_S = 600;

/**
 * How long an unacknowledged abort is worth resending, in seconds.
 *
 * The server undoes a join the browser never finished at its own `join_deadline` (a day by
 * default), so past that the abort has nothing left to do -- and an entry kept for ever would spend
 * a request on every page load for a transaction that is long closed.
 */
export const ABORT_TTL_S = 86400;

/**
 * Answers that settle an abort without a `membership` in them: the server has no such transaction
 * for this fan (`404 membership_unknown_txn`, `400 membership_bad_txn`), so there is nothing to undo.
 */
const NOTHING_TO_ABORT = [ 400, 404 ];

/**
 * @param {Object}   options                   Wiring.
 * @param {Object}   options.transport         ./transport.js.
 * @param {Object}   options.marker            ./marker.js.
 * @param {Object}   options.lock              ./lock.js.
 * @param {Object}   options.leg               ./processorLeg.js.
 * @param {Function} options.identity          `() => sub|''`: the fan signed in right now.
 * @param {Function} options.consentGranted    `() => boolean`: the consent machine's current answer.
 * @param {Function} options.ensureConsentRead `() => Promise`: settles once consent has been read.
 * @param {Function} options.onAnswer          `({granted, checkedAt}) => void`: hand a server-confirmed
 *                                             value to the consent machine.
 * @param {Function} options.onSettled         `(sub) => void`: drop per-fan derived caches.
 * @param {Function} options.now               Epoch ms.
 * @param {Function} options.uuid              A v4 UUID.
 * @param {boolean}  options.eraseData         Leave policy.
 * @param {Function} [options.warn]            Logger.
 * @return {Object} The membership client.
 */
export function createMembership( {
	transport,
	marker,
	lock,
	leg,
	identity,
	consentGranted,
	ensureConsentRead,
	onAnswer,
	onSettled,
	now,
	uuid,
	eraseData,
	warn = () => {},
} ) {
	/**
	 * Take a server answer in: the marker, the consent machine, the derived caches.
	 *
	 * An answer about a SUPERSEDED transaction does not overwrite a marker that already names a
	 * newer one -- the marker tracks the fan's latest intent, and an old leg's late report is not
	 * that. Where the marker still names the superseded one (another device superseded it), it is
	 * written as it came: closed, no task, and the next status read brings the current one.
	 *
	 * @param {string} sub    The fan.
	 * @param {Object} answer A transport result.
	 * @return {void}
	 */
	function absorb( sub, answer ) {
		const summary = answer.membership;

		if ( summary ) {
			const current = marker.read( sub );
			const stale = summary.state === 'superseded' && current && current.txn !== summary.txn;

			if ( ! stale ) {
				marker.write( sub, summary );
			}

			if ( ! summary.open || summary.state === 'superseded' ) {
				onSettled( sub );
			}
		}

		if ( typeof answer.granted === 'boolean' ) {
			onAnswer( { granted: answer.granted, checkedAt: answer.checkedAt } );
		}
	}

	/**
	 * @param {Object} out A leg result.
	 * @return {string|null} What to report, or null for "report nothing".
	 */
	function resultOf( out ) {
		if ( ! out ) {
			return null;
		}

		if ( out.result === LEG.FENCED ) {
			// Another fan is signed in now: their token would carry this report, and the server
			// would refuse it as a foreign transaction anyway. Say nothing.
			return out.fence === 'identity' ? null : 'retry';
		}

		return out.result;
	}

	/**
	 * @param {string} sub      The fan the leg was started for.
	 * @param {string} txn      Its transaction.
	 * @param {string} task     Its task.
	 * @param {Object} [signal] Abort signal.
	 * @return {Function} The fence.
	 */
	function fenceFor( sub, txn, task, signal ) {
		return () => {
			if ( signal && signal.aborted ) {
				throw new FenceError( 'aborted' );
			}

			if ( identity() !== sub ) {
				throw new FenceError( 'identity' );
			}

			const current = marker.read( sub );

			if ( current && current.txn !== txn ) {
				throw new FenceError( 'superseded' );
			}

			if ( task === 'join' && ! consentGranted() ) {
				throw new FenceError( 'consent' );
			}
		};
	}

	const api = {
		/**
		 * @return {boolean} Whether this page can run transactions at all.
		 */
		available() {
			return Boolean( transport.available );
		},

		/**
		 * Begin a join or a leave.
		 *
		 * The id is minted HERE and resent unchanged on a transport failure, so a begin whose answer
		 * was lost is the same transaction on the second try -- and can be aborted by id if the fan
		 * gives up.
		 *
		 * @param {string} op 'join' or 'leave'.
		 * @return {Promise<Object>} `{ok, fallback, txn, membership, granted, reason, status}`.
		 */
		async begin( op ) {
			if ( ! api.available() ) {
				return {
					ok: false,
					fallback: true,
					txn: '',
					membership: null,
					granted: null,
					reason: 'unconfigured',
				};
			}

			const sub = identity();

			if ( ! sub ) {
				return {
					ok: false,
					fallback: false,
					txn: '',
					membership: null,
					granted: null,
					reason: 'no-fan',
				};
			}

			const txn = uuid();
			let answer = await transport.begin( txn, op );

			if ( ! answer.membership && RESENDABLE.includes( answer.reason ) ) {
				answer = await transport.begin( txn, op );
			}

			if ( ! answer.membership && FALLBACK.includes( answer.reason ) ) {
				return {
					ok: false,
					fallback: true,
					txn,
					membership: null,
					granted: null,
					reason: answer.reason,
				};
			}

			absorb( sub, answer );

			return {
				ok: Boolean( answer.membership ),
				fallback: false,
				txn: answer.membership ? answer.membership.txn : txn,
				membership: answer.membership,
				granted: answer.granted,
				reason: answer.reason,
				status: answer.status,
				// Whether the server may hold a row for this id -- and so whether a failure the fan
				// is shown needs an abort at all. Yes when it answered with one; yes when the
				// request died on the way (a timeout or a network error after the resend), because
				// it may have landed. NO when the server answered without one (401, 403, 429, a
				// 503 before the insert) or the request never left: there is nothing to undo, and
				// queueing an abort for it would resend a doomed request on every page load.
				mayExist:
					Boolean( answer.membership ) ||
					( answer.status === 0 && RESENDABLE.includes( answer.reason ) ),
			};
		},

		/**
		 * Run a transaction's 3Halves leg, under the fan's lock and a fence.
		 *
		 * @param {Object}  summary                     The server's summary.
		 * @param {Object}  [opts]                      Options.
		 * @param {Object}  [opts.signal]               Abort signal.
		 * @param {boolean} [opts.wait]                 Wait for the lock rather than skip.
		 * @param {boolean} [opts.mustVerifyWithdrawal] See ./processorLeg.js.
		 * @return {Promise<Object|null>} The leg result, `{skipped: true}`, or null for no task.
		 */
		async run( summary, { signal, wait = false, mustVerifyWithdrawal = false } = {} ) {
			const sub = identity();

			if ( ! sub || ! summary || ! summary.task ) {
				return null;
			}

			const out = await lock.run(
				sub,
				() =>
					leg.run( summary.task, {
						fence: fenceFor( sub, summary.txn, summary.task, signal ),
						signal,
						eraseData,
						mustVerifyWithdrawal: mustVerifyWithdrawal || summary.after === 'leave',
					} ),
				{ wait, signal }
			);

			return out === SKIPPED ? { skipped: true } : out;
		},

		/**
		 * Report a leg to the server and take its answer in.
		 *
		 * @param {string} txn      The transaction.
		 * @param {Object} out      The leg result.
		 * @param {string} [result] Override: a dialog reports `abort` for what a resume reports as
		 *                          `retry`, because the fan was told it failed.
		 * @return {Promise<Object|null>} The server's answer, or null when nothing was sent.
		 */
		async report( txn, out, result ) {
			const sub = identity();
			const verdict = result || resultOf( out );

			if ( ! sub || ! txn || ! verdict ) {
				return null;
			}

			const answer = await transport.advance( txn, {
				result: verdict,
				code: out ? out.code : '',
				wrote: Boolean( out && out.wrote ),
			} );

			absorb( sub, answer );

			return answer;
		},

		/**
		 * Give up on a transaction the fan was told failed. Best effort and never rejects: a begin
		 * whose answer never arrived is aborted by its id, and the server undoes whatever it did.
		 *
		 * @param {string} txn  The transaction.
		 * @param {string} code Why.
		 * @return {Promise<Object|null>} The server's answer, or null.
		 */
		abort( txn, code ) {
			const sub = identity();

			// Recorded BEFORE sending, cleared once the server has answered for it: an abort that
			// never arrives must not leave a transaction the fan was told failed to be finished by
			// a later page's resume.
			if ( sub && txn ) {
				marker.setPendingAbort( sub, { txn, code, at: Math.floor( now() / 1000 ) } );
			}

			return api
				.report( txn, { code, wrote: false }, 'abort' )
				.then( answer => {
					const settled =
						answer &&
						( answer.membership || NOTHING_TO_ABORT.includes( answer.status ) );

					if ( sub && settled ) {
						marker.setPendingAbort( sub, null );
					}

					return answer;
				} )
				.catch( () => null );
		},

		/**
		 * Remember to look once more for a late withdrawal raise after a rejoin.
		 *
		 * @return {void}
		 */
		noteRejoin() {
			const sub = identity();

			if ( sub ) {
				marker.patch( sub, { verifyUntil: Math.floor( now() / 1000 ) + VERIFY_WINDOW_S } );
			}
		},

		/**
		 * On a page load: run a leg that is due, and the rejoin's one extra look. Never rejects.
		 *
		 * Costs no request at all when the marker has nothing due -- which is every page for every
		 * fan who has not just joined or left.
		 *
		 * @return {Promise<void>} Settles when done.
		 */
		async resume() {
			try {
				const sub = identity();
				const nowSec = Math.floor( now() / 1000 );
				let unsent = sub ? marker.pendingAbort( sub ) : null;

				// Past its TTL the server's own deadline has undone it: drop it unsent. An entry
				// with no time on it predates the TTL and goes the same way.
				if ( unsent && ! ( nowSec - Number( unsent.at || 0 ) < ABORT_TTL_S ) ) {
					marker.setPendingAbort( sub, null );
					unsent = null;
				}

				// An abort that never reached the server goes first, and its transaction is never
				// finished here: the fan was told it failed.
				if ( unsent ) {
					await api.abort( unsent.txn, unsent.code );
				}

				const due = sub ? marker.read( sub ) : null;

				if ( isDue( due, nowSec ) && ! ( unsent && unsent.txn === due.txn ) ) {
					if ( due.task === 'join' ) {
						// The join's fence reads the consent machine, which may not have answered
						// yet on a fresh page.
						await ensureConsentRead();
					}

					const out = await api.run( due, { wait: false } );

					if ( out && ! out.skipped ) {
						await api.report( due.txn, out );
					}
				}

				const after = sub ? marker.read( sub ) : null;

				if ( wantsVerify( after, nowSec ) && ! after.open ) {
					const out = await lock.run(
						sub,
						() =>
							leg.run( 'cancel', {
								fence: fenceFor( sub, after.txn, 'cancel' ),
								eraseData,
							} ),
						{ wait: false }
					);

					if ( out && out !== SKIPPED && out.result === LEG.DONE ) {
						marker.patch( sub, { verifyUntil: 0 } );
					}
				}
			} catch ( error ) {
				warn( 'resuming a membership leg failed', error );
			}
		},
	};

	return api;
}
